import React, { useState, useRef, useEffect } from "react";
import Popover from "@mui/material/Popover";
import constants from '../../constants';
import Badge from './Badge';
import Tooltip from "./Tooltip";

const SharedScopeBadge = ({ shareScope, sharedCompanies = [] }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [popoverPlacement, setPopoverPlacement] = useState({
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        transformOrigin: { vertical: 'top', horizontal: 'center' },
        paperSx: {},
    });
    const [lastTrigger, setLastTrigger] = useState(null);
    const closeTimeoutRef = useRef(null);

    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        };
    }, []);

    const isSpecific = shareScope === constants.SHARED_SCOPE.SPECIFIC;

    const shareScopeText = shareScope === constants.SHARED_SCOPE.PUBLIC
        ? 'Shared with All'
        : shareScope === constants.SHARED_SCOPE.PRIVATE
            ? 'Private'
            : `Shared with ${sharedCompanies?.length || 0}`;

    const color = shareScope === constants.SHARED_SCOPE.PUBLIC
        ? 'success'
        : shareScope === constants.SHARED_SCOPE.PRIVATE
            ? 'grey'
            : 'purple';

    const handleMouseEnter = (event) => {
        if (!isSpecific) return;

        // clear any pending close
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }

        const trigger = event.currentTarget;
        setLastTrigger(trigger);
        setAnchorEl(trigger);

        // compute available space and choose placement to avoid covering the badge
        const rect = trigger.getBoundingClientRect();
        const margin = 12;
        const preferredHeight = 180; // approximate preferred popover height
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const spaceRight = window.innerWidth - rect.right;
        const spaceLeft = rect.left;

        let anchorOrigin = { vertical: 'bottom', horizontal: 'center' };
        let transformOrigin = { vertical: 'top', horizontal: 'center' };
        let paperSx = {};

        if (spaceBelow >= preferredHeight + margin) {
            // enough space below
            anchorOrigin = { vertical: 'bottom', horizontal: 'center' };
            transformOrigin = { vertical: 'top', horizontal: 'center' };
            paperSx = { marginTop: '4px' };
        } else if (spaceAbove >= preferredHeight + margin) {
            // show above the badge
            anchorOrigin = { vertical: 'top', horizontal: 'center' };
            transformOrigin = { vertical: 'bottom', horizontal: 'center' };
            paperSx = { marginBottom: '6px' };
        } else {
            // constrained vertically — pick side (left/right) that has more space
            if (spaceRight > spaceLeft) {
                anchorOrigin = { vertical: 'center', horizontal: 'right' };
                transformOrigin = { vertical: 'center', horizontal: 'left' };
                paperSx = { marginLeft: '8px' };
            } else {
                anchorOrigin = { vertical: 'center', horizontal: 'left' };
                transformOrigin = { vertical: 'center', horizontal: 'right' };
                paperSx = { marginRight: '8px' };
            }
        }

        setPopoverPlacement({ anchorOrigin, transformOrigin, paperSx });
    };

    const handleMouseLeave = () => {
        // delay close slightly to allow pointer to enter the popover
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = setTimeout(() => {
            setAnchorEl(null);
            closeTimeoutRef.current = null;
        }, 120);
    };

    return (
        <div 
            className="sharedScopeContainer" 
            style={{ position: 'relative', display: 'inline-block' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="sharedScopeSelector">
                <Badge
                    size="sm"
                    label={shareScopeText}
                    color={color}
                    style={{ cursor: isSpecific ? 'help' : 'default' }}
                />
            </div>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleMouseLeave}
                disableRestoreFocus
                anchorOrigin={popoverPlacement.anchorOrigin}
                transformOrigin={popoverPlacement.transformOrigin}
                sx={{
                    pointerEvents: 'none',
                    '& .MuiPopover-paper': {
                        pointerEvents: 'auto',
                        backgroundColor: 'white',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        padding: '0',
                        border: '1px solid #e5e7eb',
                        ...popoverPlacement.paperSx,
                    },
                }}
            >
                <div
                    className="sharedScopeBadgePopover"
                    onMouseEnter={() => {
                        if (closeTimeoutRef.current) {
                            clearTimeout(closeTimeoutRef.current);
                            closeTimeoutRef.current = null;
                        }
                        if (lastTrigger) setAnchorEl(lastTrigger);
                    }}
                    onMouseLeave={() => {
                        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
                        closeTimeoutRef.current = setTimeout(() => setAnchorEl(null), 120);
                    }}
                >
                    <div className="sharedScopeBadgeTitle">
                        <span>Shared with:</span>
                    </div>
                    <div className="sharedScopeBadgeContent">
                        {sharedCompanies && sharedCompanies.map((company, index) => (
                            <Tooltip 
                                key={company.id || index} 
                                title={company.company_name}
                                maxChars={10}
                            >
                                <Badge
                                    size="lg"
                                    label={company.company_name}
                                    color={"purple"}
                                />
                            </Tooltip>
                        ))}
                    </div>
                </div>
            </Popover>
        </div>
    );
};

export default SharedScopeBadge;